import { LoggedOutHomeImage } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeImage/LoggedOutHomeImage";
import { BrandInfo } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/__internal__/useGetBrandValues";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";

import { useIsLoggedOutHomepageV2 } from "../../../useIsLoggedOutHomepageV2";

import {
  imageWrapperHeightVars,
  MOBILE_BANNER_HEIGHT,
  DESKTOP_BANNER_HEIGHT,
  TABLET_BANNER_HEIGHT,
  XL_DESKTOP_BANNER_HEIGHT,
} from "./styles.css";

export const BrandValueBannerBrandInfo: React.FC<{
  brandInfo: BrandInfo | undefined;
  className?: string;
}> = ({ brandInfo, className }) => {
  if (!brandInfo) {
    return null;
  }
  return (
    <Flex
      align={{ mobileAndAbove: "flex-start", desktopAndAbove: "center" }}
      direction={{ mobileAndAbove: "column", tabletAndAbove: "row" }}
      className={cn("w-full xl:w-[1088px]", className)}
      gap={{ mobileAndAbove: "4x", tabletAndAbove: "6x" }}
    >
      <LoggedOutHomeImage
        lazyLoad
        imageWrapperStyles={imageWrapperHeightVars.vars}
        className={cn("w-full md:w-1/2", imageWrapperHeightVars.classes)}
        responsiveImage={{
          mobileAndAbove: IImage.build({
            url: brandInfo.image,
          }),
        }}
        optimizeImageOptions={{
          mobileAndAbove: {
            width: MOBILE_BANNER_HEIGHT,
            height: MOBILE_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
          tabletAndAbove: {
            width: TABLET_BANNER_HEIGHT,
            height: TABLET_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
          desktopAndAbove: {
            width: DESKTOP_BANNER_HEIGHT,
            height: DESKTOP_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
          xLargeAndAbove: {
            width: XL_DESKTOP_BANNER_HEIGHT,
            height: XL_DESKTOP_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
        }}
      />
      <RightColumn brandInfo={brandInfo} />
    </Flex>
  );
};

const RightColumn: React.FC<{
  brandInfo: BrandInfo;
}> = ({ brandInfo }) => {
  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  const { name, location, description, cta } = brandInfo;
  return (
    <Column justify="center" className="w-full md:w-1/2">
      <Typography variant="sectionHeaderSansMedium">{name}</Typography>
      <Spacer size="1x" />
      <Typography variant="paragraphSansRegular">{location}</Typography>
      <Spacer size="2x" />
      <Typography
        variant="subheadingSansRegular"
        className="max-w-full lg:max-w-[350px]"
      >
        {description}
      </Typography>
      <Spacer size="2x" />
      <Button
        variant="secondary"
        style={{ width: "fit-content" }}
        linkTo={cta.link}
        className="hover:bg-core-surface-default! bg-transparent!"
        onClick={() => {
          trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
            "brand_value_banner-cta",
            cta.link,
            isHomepageRedesignV2 ? "v2" : "v1"
          );
        }}
      >
        {cta.name}
      </Button>
    </Column>
  );
};
